<template>
  <div class="view-cases">
    <div class="header-container">
      <h3 class="page-title" data-cy="case-page-title">
        {{ $t(`viewCases.page_title_${(caseType as CaseType).toLowerCase()}`) }}
      </h3>
      <BackstageButton
        icon-right="addition-sign"
        :disabled="!caseCreationEnabled"
        data-cy="new-case-create-button-cnt"
        data-cy-button="new-case-create-button"
        v-on:click="createNewCase">
        {{ $t(`viewCases.create_${(caseType as CaseType).toLowerCase()}_case_button`) }}
      </BackstageButton>
    </div>
    <div v-if="showWarrantyLegend" class="paper-container">
      <div class="legend-container">
        <div class="text">
          <strong class="question">{{ $t('viewCases.large_warranty_legend_title') }}</strong>
          <div v-html="$t('viewCases.large_warranty_legend_description')"></div>
          <br />
          <strong class="question">{{ $t('viewCases.laces_warranty_legend_title') }}</strong>
          <div v-html="$t('viewCases.laces_warranty_legend_description')"></div>
        </div>
      </div>
    </div>
    <div v-if="loading" class="loader"></div>
    <div v-else class="grid-container">
      <div v-if="cases.length > 0">
        <div class="grid-actions-container">
          <h4 class="page-title" data-cy="grid-title">{{ $t('viewCases.all_cases') }} {{ totalRows }}</h4>
          <BackstageButton
            :variant="ButtonVariant.SECONDARY"
            icon-left="trash"
            data-cy="clear-all-filters-button-cnt"
            data-cy-button="clear-all-filters-button"
            v-on:click="removeAllGridFilters()">
            {{ $t('global.grids.clear_all_filters_button') }}
          </BackstageButton>
        </div>
        <AgGridVue
          class="ag-theme-balham grid-table-container"
          :grid-options="gridOptions"
          :column-defs="columnDefinitions"
          :default-col-def="defaultColDef"
          :row-data="cases"
          :modules="modules"
          :pagination="true"
          :pagination-page-size="50"
          :suppress-menu-hide="true"
          :suppress-pagination-panel="false"
          v-on:first-data-rendered="onFirstDataRendered"
          v-on:grid-ready="onGridReady">
        </AgGridVue>
      </div>
      <div v-else class="no-rows">
        {{ $t('viewCases.no_cases_message') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useBPLogger } from '@/composables/business-process-logger';
import { useDocumentHelpers } from '@/composables/document-helpers';
import { AddEditCaseBreadcrumbSteps } from '@/models/add-edit-case-breadcrumb-steps.enum';
import { ButtonVariant } from '@/models/button-variant.enum';
import { RouteNames } from '@/router';
import { useUserStore } from '@/stores/user';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { GridApi, type ColDef } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { CaseStatus, CaseType, LegalEntityEnum, OrderDocumentStatus } from '@onrunning/backstage-models';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useFormat } from '../composables/format';
import { useGrid } from '../composables/grid';
import { useLocale } from '../composables/locale';
import { accountMixins } from '../mixins/account.mixins';
import { utilsMixins } from '../mixins/utils.mixins';
import { CaseHeader } from '../models/case-header.model';
import { DocumentType } from '../models/document-type.enum';
import { GridConfig } from '../models/grid-config';
import { GridNames } from '../models/grids-names.enum';
import { OrderStatus } from '../models/order-status.enum';
import { CasesService } from '../services/cases.service';
import { OrderDocumentService } from '../services/order-document-service';
import { useGridsStore } from '../stores/grids';

const CREDIT_NOTE_NUMBER = 'creditNoteNumber';
const RETURN_PDF = 'returnPDF';

export default defineComponent({
  name: 'view-cases',
  components: {
    AgGridVue,
    BackstageButton,
  },
  props: {
    caseType: {
      type: String,
      default: undefined,
    },
  },

  setup() {
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const { isSalesRep } = useUserStore();
    const { addEditGridConfig, getGridByName } = useGridsStore();
    const { openPDF } = useDocumentHelpers();
    const { gridNumberFormatter } = useFormat();
    const { defaultGridOptions, dateColumnDefinition, applyGridConfig } = useGrid();
    const { userLocale } = useLocale();
    const { pageViewBPLog } = useBPLogger();

    return {
      accountId: selectedAccountId,
      account: selectedAccount,
      userLocale,
      ButtonVariant,
      isSalesRep,
      addEditGridConfig,
      getGridByName,
      openPDF,
      gridNumberFormatter,
      defaultGridOptions,
      dateColumnDefinition,
      applyGridConfig,
      pageViewBPLog,
    };
  },
  data() {
    return {
      loading: true,
      caseCreationEnabled: false,
      cases: new Array<CaseHeader>(),
      casesService: new CasesService(),
      orderDocumentsService: new OrderDocumentService(),
      defaultColDef: {
        resizable: true,
        filter: true,
        sortable: true,
        minWidth: 80,
      },
      totalRows: '',
      gridOptions: {
        ...this.defaultGridOptions(),
        getRowClass: (params: any) => {
          if (params.node.data.status === CaseStatus.NotSubmitted) {
            return 'draft-highlight';
          }
        },
      },
      gridApi: undefined as GridApi | undefined,
      modules: [ClientSideRowModelModule],
      initialGridConfig: new GridConfig() as Partial<GridConfig> | undefined,
    };
  },
  computed: {
    columnDefinitions(): any[] {
      const columnDefinitions: ColDef[] = [
        {
          headerValueGetter: () => this.$t('viewCases.grid_columns.case_number'),
          field: 'caseNumber',
          suppressSizeToFit: true,
          width: 120,
          cellClass: ['clickable-cell'],
        },
        {
          headerValueGetter: () => this.$t('viewCases.grid_columns.status'),
          field: 'status',
          valueGetter: this.statusFormatter,
        },
      ];

      if (this.caseType === CaseType.Warranty) {
        columnDefinitions.push(
          {
            headerValueGetter: () => this.$t('viewCases.grid_columns.items_open'),
            field: 'itemsOpen',
            valueFormatter: this.gridNumberFormatter,
            cellStyle: { textAlign: 'right' },
          },
          {
            headerValueGetter: () => this.$t('viewCases.grid_columns.items_approved'),
            field: 'itemsApproved',
            valueFormatter: this.gridNumberFormatter,
            cellStyle: { textAlign: 'right' },
          },
          {
            headerValueGetter: () => this.$t('viewCases.grid_columns.items_declined'),
            field: 'itemsDeclined',
            valueFormatter: this.gridNumberFormatter,
            cellStyle: { textAlign: 'right' },
          },
        );
      } else if (this.caseType === CaseType.Return) {
        columnDefinitions.push(
          {
            headerValueGetter: () => this.$t('viewCases.grid_columns.items_total_quantity'),
            field: 'itemsTotalQuantity',
            valueFormatter: this.gridNumberFormatter,
            cellStyle: { textAlign: 'right' },
          },
          {
            headerValueGetter: () => this.$t('viewCases.grid_columns.return_pdf'),
            field: RETURN_PDF,
            cellRenderer: this.confirmationDocumentDownloadRenderer,
            suppressMenu: true,
            cellClass: ['clickable-cell', 'justified-center'],
            valueGetter: this.columnSortValueGetter,
          },
        );
      }

      columnDefinitions.push(
        {
          headerValueGetter: () => this.$t('viewCases.grid_columns.credit_note'),
          field: CREDIT_NOTE_NUMBER,
          cellRenderer: this.creditNoteDownloadRenderer,
          suppressMenu: true,
          cellClass: ['clickable-cell', 'justified-center'],
          valueGetter: this.columnSortValueGetter,
        },
        this.dateColumnDefinition({
          headerValueGetter: () => this.$t('viewCases.grid_columns.created_date').toString(),
          field: 'createdAt',
          sort: 'desc',
        }),
        {
          headerValueGetter: () => this.$t('viewCases.grid_columns.on_reference').toString(),
          field: 'returnOrder.orderNumber',
        },
      );

      return columnDefinitions;
    },
    showWarrantyLegend(): boolean {
      return (
        this.caseType === CaseType.Warranty &&
        [LegalEntityEnum.ON_AG, LegalEntityEnum.ON_UK].includes(
          this.account?.legalEntity?.toLocaleLowerCase() as LegalEntityEnum,
        )
      );
    },
    gridName(): GridNames {
      return this.caseType === CaseType.Warranty ? GridNames.WarrantyHeaders : GridNames.ReturnHeaders;
    },
  },
  watch: {
    caseType() {
      this.caseCreationEnabled = false;
      this.initialGridConfig = this.getGridByName(this.gridName);

      this.setCaseCreationEnabled();
      this.fetchGridData();
    },
    account: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setCaseCreationEnabled();
        this.accountId = newValue;
        this.initialGridConfig = undefined;
        this.fetchGridData();
      }
    },
    userLocale(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.gridApi?.refreshHeader();
          this.gridApi?.redrawRows();
        });
      }
    },
  },
  mounted() {
    this.initialGridConfig = this.getGridByName(this.gridName);

    this.fetchGridData();
    this.setUpEventListeners();
    this.setCaseCreationEnabled();
    this.gridOptions.onPaginationChanged = this.onPaginationChanged;
    this.gridOptions.onFilterChanged = this.gridFilterEventHandler;
    this.gridOptions.onSortChanged = this.onSortChanged;
    console.log(this.pageViewBPLog(true, { page_name: 'view-cases' }));
  },
  beforeUnmount() {
    this.tearDownEventListeners();
    this.gridApi = undefined;
  },
  methods: {
    ...utilsMixins,
    ...accountMixins,
    setUpEventListeners() {
      window.addEventListener('resize', this.handleResize);
    },
    tearDownEventListeners() {
      window.removeEventListener('resize', this.handleResize);
    },
    async setCaseCreationEnabled() {
      this.caseCreationEnabled = await this.accountHasCaseCreationEnabled(this.caseType as CaseType, this.accountId);
    },
    async fetchGridData() {
      this.loading = true;
      this.totalRows = '';
      const draftValidation = await this.casesService.getCasesHeaders(this.caseType as CaseType, this.accountId);

      if (draftValidation.model) {
        this.cases = draftValidation.model;
        this.cases.forEach((caseOrder: CaseHeader) => {
          caseOrder.createdAt = new Date(caseOrder.createdAt);
        });
      } else {
        // TODO: show error
        return;
      }

      this.loading = false;
    },
    onGridReady(params: any) {
      this.gridApi = params.api;
      params.api.addEventListener('cellClicked', this.onCellClick);

      if (this.initialGridConfig) {
        this.applyGridConfig(this.initialGridConfig, this.gridApi as GridApi);
      }
    },
    onFirstDataRendered(params: any) {
      params.api.sizeColumnsToFit();
    },
    async onCellClick(params: any) {
      if (params.colDef.field === 'caseNumber') {
        this.openCase(params.data.id);
      } else if (params.colDef.field === CREDIT_NOTE_NUMBER && params.data.creditNoteNumber) {
        this.showLoadingIcon(params, true, CREDIT_NOTE_NUMBER);
        const validation = await this.orderDocumentsService.get(
          params.data.returnOrder.orderNumber,
          params.data.creditNoteNumber,
          this.accountId,
          DocumentType.CreditNote,
        );

        if (validation.isValid() && validation.model?.base64Body) {
          this.openPDF(validation.model.base64Body, params.data.creditNoteNumber, params.data.returnOrder?.orderNumber);
        } else {
          this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));
        }

        this.showLoadingIcon(params, false, CREDIT_NOTE_NUMBER);
      } else if (
        params.colDef.field === RETURN_PDF &&
        [OrderStatus.Backorder, OrderStatus.Invoiced, OrderStatus.Delivered].includes(
          params.data.returnOrder?.orderStatus,
        )
      ) {
        this.showLoadingIcon(params, true, RETURN_PDF);

        const validation = await this.orderDocumentsService.get(
          params.data.returnOrder.orderNumber,
          params.data.returnOrder.orderNumber,
          this.accountId,
          DocumentType.OrderConfirmation,
        );

        if (validation.isValid() && validation.model?.base64Body) {
          this.openPDF(validation.model.base64Body, params.data.caseNumber);
        } else {
          this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));
        }

        this.showLoadingIcon(params, false, RETURN_PDF);
      }
    },
    setDateFilter() {
      const filterComponent = this.gridApi?.getFilterInstance('createdAt');
      const dateTo = moment().add(1, 'days');
      const dateFrom = new Date();

      dateFrom.setMonth(dateFrom.getMonth() - 12);

      filterComponent?.setModel({
        type: 'inRange',
        dateFrom: dateFrom.toISOString().slice(0, 10),
        dateTo: dateTo.toISOString().slice(0, 10),
      });
      this.gridApi?.onFilterChanged();
    },
    handleResize() {
      this.gridApi?.sizeColumnsToFit();
    },
    statusFormatter(params: any): string {
      return params.data.status
        ? this.$t(`viewCase.case_status['${params.data.status.toLowerCase()}']`).toString()
        : '';
    },
    createNewCase() {
      if (this.isSalesRep()) {
        return this.$snotify.warning(this.$t('addCase.sales_rep_disabled'));
      }
      this.$router.push({
        name:
          this.caseType === CaseType.Warranty
            ? RouteNames.CreateWarrantyCaseDefault
            : RouteNames.CreateReturnCaseDefault,
      });
    },
    openCase(id: string) {
      const path = `/${(this.caseType as CaseType).toLowerCase()}-cases/${id}/${AddEditCaseBreadcrumbSteps.details}`;

      this.$router.push({ path });
    },
    removeAllGridFilters() {
      this.gridApi?.setFilterModel(null);
    },
    shouldEnableDocumentDownloadButton(params: any) {
      return (
        [OrderStatus.Backorder, OrderStatus.Invoiced, OrderStatus.Delivered].includes(
          params.data.returnOrder?.orderStatus,
        ) &&
        [OrderDocumentStatus.Confirmation, OrderDocumentStatus.Invoice].includes(
          params.data.returnOrder?.orderDocumentStatus,
        )
      );
    },
    confirmationDocumentDownloadRenderer(params: any) {
      if (this.shouldEnableDocumentDownloadButton(params)) {
        return `<button class="button download-button ${
          params.data.loadingConfirmation ? 'submitting' : ''
        }"></button>`;
      }

      return '';
    },
    creditNoteDownloadRenderer(params: any) {
      if (params.data.creditNoteNumber) {
        return `<button class="button download-button ${params.data.loadingCreditNote ? 'submitting' : ''}"></button>`;
      } else {
        return '';
      }
    },
    columnSortValueGetter(params: any) {
      if (
        params.data.creditNoteNumber ||
        [OrderStatus.Backorder, OrderStatus.Invoiced, OrderStatus.Delivered].includes(
          params.data.returnOrder?.orderStatus,
        )
      ) {
        return 1;
      } else {
        return 0;
      }
    },
    showLoadingIcon(params: any, isLoading: boolean, columnName: string) {
      if (columnName === RETURN_PDF) {
        params.data.loadingConfirmation = isLoading;
      } else if (columnName === CREDIT_NOTE_NUMBER) {
        params.data.loadingCreditNote = isLoading;
      }

      params.node.setData(params.data);
      this.$nextTick(() => this.gridApi?.redrawRows());
    },
    onPaginationChanged(gridOptions: { api?: GridApi }) {
      if (gridOptions.api) {
        this.totalRows = `( ${gridOptions.api.paginationGetRowCount() ?? ''} )`;
        this.addEditGridConfig({
          name: this.gridName,
          pageNumber: gridOptions.api.paginationGetCurrentPage(),
        });
      }
    },
    gridFilterEventHandler(params: { api?: GridApi }) {
      const currentFilterModel = params.api?.getFilterModel();

      if (currentFilterModel) {
        this.addEditGridConfig({ name: this.gridName, filters: currentFilterModel });
      }
    },
    onSortChanged() {
      const sortedColumn = this.gridApi?.getColumnState();

      if (sortedColumn) {
        this.addEditGridConfig({ name: this.gridName, sorting: sortedColumn });
      }
    },
  },
});
</script>
<style lang="scss">
.view-cases {
  @include common-container;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    padding: 0.5rem 0rem 1.5rem 0rem;

    .page-title {
      font-size: 1.25rem;
      font-weight: 400;
      text-align: left;
    }
  }

  .paper-container {
    margin-bottom: 2.25rem;
    padding: 1.625rem 1.25rem;
    width: 100%;
    background-color: var(--primary-bg-color);

    .legend-container {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 20px;

      .text {
        text-align: left;
        display: flex;
        flex-direction: column;
        font-size: 0.875rem;

        .question {
          padding-bottom: 0.25rem;
          font-size: 1rem;
        }

        a {
          text-decoration: underline;
        }
      }
    }
  }

  .grid-container {
    background-color: var(--primary-bg-color);
    border-radius: 0.25rem;

    .grid-actions-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1.25rem;
    }

    .grid-table-container {
      padding: 1.25rem 1.25rem 0;
      margin-bottom: 2.5rem;

      .draft-highlight {
        background: var(--row-highlight-color) !important;
      }
    }

    .no-rows {
      padding: 40px 0;
    }

    button.download-button {
      height: 1.3rem;
      width: 1.3rem;
      padding: 0;
      cursor: pointer;
      margin: auto;

      &.submitting {
        background: url('@/assets/img/cloud-loader.webp') no-repeat center center;
        background-size: contain;
        cursor: initial;
      }
    }
  }

  @include breakpoint(small) {
    .grid-container {
      margin: 0 0.3125rem;
      .grid-table-container {
        padding: 0.625rem 0.625rem 0;
        margin-bottom: 2.5rem;
      }
    }
  }
}
</style>
